import { put, select, takeEvery } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { featureFlagsSelectors } from '../feature-flags';

import { Saga } from 'redux-saga';
import { CoreActionCreator } from '../core/resourceStoreTypes';
import * as accountsActions from './accountsActions';
import { getLastSyncDate } from './accountsSelectors';
import * as accountsTransformers from './accountsTransformers';
import { Account, AccountProps } from './accountsTypes';

const mkQcsSyncAccountsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<AccountProps>();

const mkResourceConfig = (successAction: CoreActionCreator<any, any>, failureAction: CoreActionCreator<any, any>) =>
  mkQcsSyncAccountsConfig({
    resourceName: 'account',
    resourceBaseUrl: '/accounts',
    getLastSyncDate,

    transformResponseToResources: accountsTransformers.transformResponseToAccounts,
    transformResourceToRequestData: accountsTransformers.transformAccountToRequestData,
    transformResourcesToRequestData: accountsTransformers.transformAccountsToRequestData,

    successAction,
    failureAction,
  });

export function* getAccountsActionWatcher() {
  const resourceConfig = mkResourceConfig(accountsActions.getAccountsSuccess, accountsActions.getAccountsFailure);
  yield resourceSagaUtils.takeAndDebounce(
    accountsActions.getAccounts,
    resourceSagaUtils.qcsSyncGetResources,
    resourceConfig,
  );
}

export function* createAccountActionWatcher() {
  const resourceConfig = mkResourceConfig(accountsActions.createAccountSuccess, accountsActions.createAccountFailure);
  yield takeEvery(
    accountsActions.createAccount.type,
    resourceSagaUtils.qcsSyncCreateResource<AccountProps>,
    resourceConfig,
  );
}

export function* updateAccountActionWatcher() {
  const resourceConfig = mkResourceConfig(accountsActions.updateAccountSuccess, accountsActions.updateAccountFailure);
  yield takeEvery(
    accountsActions.updateAccount.type,
    resourceSagaUtils.qcsSyncUpdateResource<AccountProps>,
    resourceConfig,
  );
}

export function* deleteAccountActionWatcher() {
  const resourceConfig = mkResourceConfig(accountsActions.deleteAccountSuccess, accountsActions.deleteAccountFailure);
  yield takeEvery(
    accountsActions.deleteAccount.type,
    resourceSagaUtils.qcsSyncDeleteResource<AccountProps>,
    resourceConfig,
  );
}

export function* batchAccountsActionWatcher() {
  const resourceConfig = mkResourceConfig(accountsActions.batchAccountsSuccess, accountsActions.batchAccountsFailure);
  yield takeEvery(
    accountsActions.batchAccounts.type,
    resourceSagaUtils.qcsSyncUpdateResources<AccountProps>,
    resourceConfig,
  );
}

// =================================================================================================
// Account Actions
// =================================================================================================

//   id: accountId to impact
//   action: 'close' || 'disconnect' || 'make-manual'
export function* performAccountActionWatcher() {
  const config = mkQcsSyncAccountsConfig({
    resourceName: 'accounts',
    resourceBaseUrl: (action) => `/accounts/${action.payload.id}/${action.payload.qcsAction}`,
    successAction: accountsActions.performAccountActionSuccess,
    failureAction: accountsActions.performAccountActionFailure,
    // @ts-expect-error: I think we just don't ever actually call this?
    transformResourceToRequestData: () => undefined, // no request body
    transformResourceWithResponse: (_resource, response) =>
      response.data?.resource ? accountsTransformers.transformQcsAccountToAccount(response.data.resource) : undefined,
  });
  yield takeEvery(
    accountsActions.performAccountAction.type,
    resourceSagaUtils.qcsSyncUpdateResource<AccountProps>,
    config,
  );
}

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('accountsDumpUpdater');
export function* accountsDumpUpdater(resources: Array<Account>) {
  const bumpAccounts: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpAccounts');
  if (bumpAccounts) {
    yield callFirst(accountsDumpUpdaterFull, resources);
    yield callLast(accountsNudgeUpdater as Saga);
  }
}

function* accountsDumpUpdaterFull(resources: Array<Account>) {
  yield put(
    accountsActions.applyAccountsChanges(accountsTransformers.transformQcsAccountsToAccounts(resources), {
      context: 'BUMP_CHANGE_DUMP',
      props: { doMerge: false },
    }),
  );
}

export function* accountsNudgeUpdater() {
  const bumpAccounts: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpAccounts');
  if (bumpAccounts) {
    yield put(accountsActions.getAccounts(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

function* getAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.getAccountsSuccess, function* getAccountsSuccess({ payload }) {
    yield put(
      accountsActions.applyAccountsChanges(payload.resources, {
        context: 'getAccountsSuccess',
        props: { doMerge: false },
      }),
    );
  });
}

function* createAccountSuccessActionWatcher() {
  yield takeEvery(accountsActions.createAccountSuccess, function* createAccountSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges([payload], { context: 'createAccountSuccess' }));
  });
}

function* updateAccountSuccessActionWatcher() {
  yield takeEvery(accountsActions.updateAccountSuccess, function* updateAccountSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges([payload], { context: 'updateAccountSuccess' }));
  });
}

function* deleteAccountSuccessActionWatcher() {
  yield takeEvery(accountsActions.deleteAccountSuccess, function* deleteAccountSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges([payload], { context: 'deleteAccountSuccess' }));
  });
}

function* batchAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.batchAccountsSuccess, function* batchAccountsSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges(payload, { context: 'batchAccountsSuccess' }));
  });
}

function* performAccountActionSuccessActionWatcher() {
  yield takeEvery(
    accountsActions.performAccountActionSuccess,
    function* performAccountActionSuccess({ payload }: { payload: Account }) {
      yield put(
        accountsActions.applyAccountsChanges([payload], {
          context: 'performAccountActionSuccess',
          props: { doMerge: false },
        }),
      );
    },
  );
}

export const accountsSagasObj = {
  getAccountsActionWatcher,
  getAccountsSuccessActionWatcher,
  createAccountActionWatcher,
  createAccountSuccessActionWatcher,
  updateAccountActionWatcher,
  updateAccountSuccessActionWatcher,
  deleteAccountActionWatcher,
  deleteAccountSuccessActionWatcher,
  batchAccountsActionWatcher,
  batchAccountsSuccessActionWatcher,
  performAccountActionWatcher,
  performAccountActionSuccessActionWatcher,
};

export default Object.values(accountsSagasObj);
